// A Sales Order Cart edit is a snapshot mutation, not an OrderItem PUT.
// Resolve a missing supplier only for the exact persisted custom-product line
// when the user saves Edit Item. No lookups on cart load, previews, or polls.
const clean = (value) => String(value ?? "").trim();
const nameKey = (value) => clean(value).replace(/\s+/g, " ").toLowerCase();
const hasId = (value) => clean(value) !== "";

const firstInstanceId = (order) => {
  let ids = order?.InstanceUniqueIds ?? order?.instanceUniqueIds ?? [];
  if (typeof ids === "string") {
    try { ids = JSON.parse(ids); } catch (_error) { ids = [ids]; }
  }
  const first = Array.isArray(ids) ? ids[0] : ids;
  return clean(first?.instanceUniqueId ?? first);
};

export const recoverMissingCustomSupplierOnSalesOrderEdit = async ({
  source, originalProduct, updatedProduct, order, orderId, repair, logger = console,
}) => {
  if (source !== "sales-order-cart" || typeof repair !== "function") return updatedProduct;
  const pid = clean(originalProduct?.ProductId ?? originalProduct?.productId);
  if (!/^CUSTOM-[1-9]\d*$/i.test(pid)) return updatedProduct;
  if (clean(updatedProduct?.ProductId ?? updatedProduct?.productId ?? pid) !== pid)
    return updatedProduct;

  const copied = clean(originalProduct?.cartLineId ?? originalProduct?.CartLineId)
    .startsWith("product-copy:") || Boolean(originalProduct?.meta?.copiedFromCartLineId);
  if (copied) return updatedProduct;

  const persistedId = Number(originalProduct?.persistedOrderItemId ??
    originalProduct?.PersistedOrderItemId ?? originalProduct?.OrderItemId ??
    originalProduct?.orderItemId ?? originalProduct?.dbItemId ??
    originalProduct?.DBItemId ?? 0);
  if (!Number.isSafeInteger(persistedId) || persistedId <= 0) return updatedProduct;

  // A copied SO line can retain the original OrderItem as lineage. Never
  // treat that lineage as permission to alter the original line's supplier.
  const sourceId = originalProduct?.sourceOrderItemId ??
    originalProduct?.originalOrderItemId ??
    originalProduct?.source?.sourceOrderItemId;
  if (hasId(sourceId) && clean(sourceId) !== String(persistedId)) return updatedProduct;

  const expectedName = clean(originalProduct?.SupplierName ?? originalProduct?.supplierName ??
    (typeof originalProduct?.Supplier === "string" ? originalProduct.Supplier : ""));
  const suppliedNames = [
    updatedProduct?.SupplierName,
    updatedProduct?.supplierName,
    typeof updatedProduct?.Supplier === "string"
      ? updatedProduct.Supplier : updatedProduct?.Supplier?.Name,
  ].filter((value) => nameKey(value));
  if (!expectedName || suppliedNames.some((name) =>
    nameKey(name) !== nameKey(expectedName))) return updatedProduct;

  // Do not clear a previously saved ID on a harmless form edit, and do not
  // query for a new one if the form already has a verified supplier.
  const currentId = updatedProduct?.SupplierId ?? updatedProduct?.supplierId;
  if (hasId(currentId)) return updatedProduct;
  const originalId = originalProduct?.SupplierId ?? originalProduct?.supplierId;
  if (hasId(originalId)) {
    return { ...updatedProduct, SupplierId: originalId, supplierId: originalId };
  }

  const instanceUniqueId = firstInstanceId(order);
  const projectNumber = clean(order?.OrderId ?? orderId);
  if (!instanceUniqueId || !projectNumber) return updatedProduct;

  try {
    const result = await repair(persistedId, {
      instanceUniqueId, projectNumber, productId: pid, supplierName: expectedName,
    });
    if (!hasId(result?.supplierId) || nameKey(result?.supplierName) !== nameKey(expectedName))
      return updatedProduct;
    return { ...updatedProduct, SupplierId: result.supplierId, supplierId: result.supplierId };
  } catch (error) {
    // Supplier metadata recovery should not block a valid item/pricing edit.
    logger.warn?.("[sales-order-cart:custom-supplier-repair]", error?.message || error);
    return updatedProduct;
  }
};
